// Inline Urdu editor — reusable side-by-side widget.
//
// Renders a small "اردو" panel that sits next to (or below) an English
// input in the Question / Wave editor. Fetches the current translation +
// status straight from the doc it's attached to and PATCHes back to the
// admin i18n endpoints. Self-contained — parents don't need to plumb any
// Urdu state.
//
// Behaviour:
//   • Auto-loads latest saved Urdu on mount (or accepts one via `initial`).
//   • Rings green when approved, amber when AI-draft, red when stale.
//   • Approve button POSTs the current text + status="approved".
//   • Save-draft button POSTs the current text + status="draft".
//   • Re-translate button forces a fresh Claude pass for THIS field.
//   • When the master Urdu toggle is OFF (feature disabled), the whole
//     widget hides. Same when the parent is un-saved (no ID yet).
//
// The Retranslate helper POSTs to the wave-level backfill endpoint (which
// covers ALL fields on the wave — including this one). For question rows
// there's no per-question backfill; we ask the caller to pass a
// `retranslate` callback that either backfills the wave OR does a small
// PATCH shortcut. Keep the API surface tiny.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";

function StatusPill({ status }) {
  const map = {
    missing:  { bg: "bg-neutral-200", fg: "text-neutral-700", label: "MISSING" },
    draft:    { bg: "bg-amber-100",   fg: "text-amber-800",   label: "AI DRAFT" },
    approved: { bg: "bg-emerald-100", fg: "text-emerald-800", label: "APPROVED" },
    stale:    { bg: "bg-red-100",     fg: "text-red-800",     label: "STALE" },
  };
  const s = map[status] || map.missing;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold tracking-wider ${s.bg} ${s.fg} shrink-0`}>
      {s.label}
    </span>
  );
}

/**
 * Props:
 *   entity      — "question" | "wave"
 *   entityId    — the question or wave id (null → widget hides)
 *   field       — for waves: "name" | "intro_topic" | "intro_text"
 *                 for questions: "text" OR { optionIndex: N }  (see below)
 *   enValue     — the English text (used for the "En:" hint above the box)
 *   initialUr   — pre-populated Urdu text (from parent's doc state)
 *   initialStatus — pre-populated status string
 *   compact     — when true, single-row layout (used inside option rows)
 *   onSaved     — optional callback(ur, status) after a save
 */
export default function InlineUrduEditor({
  entity, entityId, field, enValue = "",
  initialUr = "", initialStatus = "missing",
  compact = false, onSaved,
}) {
  const [ur, setUr] = useState(initialUr || "");
  const [status, setStatus] = useState(initialStatus || (initialUr ? "draft" : "missing"));
  const [busy, setBusy] = useState(false);

  // Keep local state in sync when parent re-loads the underlying doc.
  useEffect(() => { setUr(initialUr || ""); }, [initialUr]);
  useEffect(() => { setStatus(initialStatus || (initialUr ? "draft" : "missing")); }, [initialStatus, initialUr]);

  const patchPayload = useCallback((approve) => {
    if (entity === "wave") {
      return { field, ur: ur.trim(), approve };
    }
    // question
    if (typeof field === "object" && field?.optionIndex !== undefined) {
      return { options: [{ index: field.optionIndex, ur: ur.trim(), approve }] };
    }
    return { ur: ur.trim(), approve_text: approve };
  }, [entity, field, ur]);

  const doSave = async (approve) => {
    if (!entityId) return;
    const cleanUr = ur.trim();
    if (approve && !cleanUr) { toast.error("Type or draft the Urdu first."); return; }
    setBusy(true);
    try {
      const path = entity === "wave"
        ? `/admin/i18n/waves/${entityId}`
        : `/admin/i18n/questions/${entityId}`;
      await api.patch(path, patchPayload(approve));
      const nextStatus = approve ? "approved" : "draft";
      setStatus(nextStatus);
      onSaved?.(cleanUr, nextStatus);
      toast.success(approve ? "Approved for patrons." : "Saved as draft.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const retranslate = async () => {
    if (!entityId) return;
    if (!enValue?.trim()) { toast.error("English source is empty — nothing to translate."); return; }
    setBusy(true);
    try {
      let updatedUr = "";
      let updatedStatus = "draft";
      if (entity === "wave") {
        const r = await api.post(`/admin/i18n/waves/${entityId}/draft-field`);
        const w = r.data;
        updatedUr = (w?.[`${field}_ur`]) || "";
        updatedStatus = (w?.[`${field}_ur_status`]) || "draft";
      } else {
        // question
        const r = await api.post(`/admin/i18n/questions/${entityId}/draft`);
        const q = r.data;
        if (typeof field === "object" && field?.optionIndex !== undefined) {
          const opt = (q?.options || [])[field.optionIndex] || {};
          updatedUr = opt.text_ur || "";
          updatedStatus = opt.text_ur_status || "draft";
        } else {
          updatedUr = q?.text_ur || "";
          updatedStatus = q?.text_ur_status || "draft";
        }
      }
      if (updatedUr) {
        setUr(updatedUr);
        setStatus(updatedStatus);
        toast.success("AI draft generated. Review + Approve to make it live.");
      } else {
        toast.error("AI didn't return a translation — try again or type manually.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI draft failed");
    } finally { setBusy(false); }
  };

  if (!entityId) {
    // New (un-saved) entity — parent hasn't been created yet.
    return (
      <div className={compact ? "text-[10px] text-neutral-400 italic" : "text-xs text-neutral-500 italic mt-1 pl-1"}>
        اردو auto-drafts after you save.
      </div>
    );
  }

  // Compact layout — used inside option rows (single line)
  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-1 min-w-0" data-testid={`urdu-inline-${field?.optionIndex ?? field}`}>
        <input
          dir="rtl"
          lang="ur"
          value={ur}
          onChange={(e) => { setUr(e.target.value); if (status === "approved") setStatus("draft"); }}
          disabled={busy}
          className="border border-neutral-300 px-2 py-1.5 text-sm flex-1 min-w-0 focus:border-black focus:outline-none"
          style={{ fontFamily: "'Noto Naskh Arabic', system-ui, sans-serif", direction: "rtl" }}
          placeholder="Urdu translation"
          data-testid={`urdu-input-${field?.optionIndex ?? field}`}
        />
        <StatusPill status={status} />
        <Button
          type="button" size="sm" variant="outline" disabled={busy || !enValue?.trim()}
          onClick={retranslate}
          className="rounded-none h-8 px-2 text-[11px] border-mango text-mango hover:bg-mango/10"
          data-testid={`urdu-ai-${field?.optionIndex ?? field}`}
          title="Draft this option with AI (Claude)"
        >
          🪄 AI
        </Button>
        <Button
          type="button" size="sm" variant="outline" disabled={busy}
          onClick={() => doSave(true)}
          className={`rounded-none h-8 px-2 text-[11px] ${status === "approved" ? "border-emerald-600 text-emerald-700 bg-emerald-50" : "border-black"}`}
          data-testid={`urdu-approve-${field?.optionIndex ?? field}`}
          title="Approve for patrons"
        >
          {status === "approved" ? "✓" : "Approve"}
        </Button>
      </div>
    );
  }

  // Full layout — used beside the main question text / wave field
  return (
    <div className="mt-2" data-testid={`urdu-inline-${entity}-${typeof field === "string" ? field : "opt"}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-mono text-neutral-500 tracking-wider">اردو TRANSLATION</span>
        <StatusPill status={status} />
        {enValue && (
          <span className="text-[10px] text-neutral-400 truncate">EN: {enValue.slice(0, 60)}{enValue.length > 60 ? "…" : ""}</span>
        )}
      </div>
      <div className="border border-neutral-300 bg-white flex items-stretch">
        <textarea
          dir="rtl"
          lang="ur"
          rows={2}
          value={ur}
          onChange={(e) => { setUr(e.target.value); if (status === "approved") setStatus("draft"); }}
          disabled={busy}
          placeholder="اردو ترجمہ یہاں لکھیں (خالی چھوڑنے پر AI مدد کرے گا)"
          className="flex-1 px-3 py-2 text-base focus:outline-none focus:bg-emerald-50/30"
          style={{ fontFamily: "'Noto Naskh Arabic', system-ui, sans-serif", lineHeight: 1.75, direction: "rtl" }}
          data-testid={`urdu-input-${entity}-${typeof field === "string" ? field : "opt"}`}
        />
        <div className="flex flex-col border-l border-neutral-200 shrink-0">
          <button
            type="button"
            disabled={busy || !ur.trim()}
            onClick={() => doSave(true)}
            className={`px-3 py-1 text-xs font-semibold border-b border-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed ${status === "approved" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200" : "bg-black text-white hover:bg-neutral-800"}`}
            data-testid={`urdu-approve-${entity}-${typeof field === "string" ? field : "opt"}`}
          >
            {status === "approved" ? "✓ Approved" : "Approve"}
          </button>
          <button
            type="button"
            disabled={busy || !ur.trim() || status === "draft"}
            onClick={() => doSave(false)}
            className="px-3 py-1 text-[10px] text-neutral-500 hover:text-black hover:bg-neutral-50 border-b border-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Save without approving — patrons still see English"
          >
            Save draft
          </button>
          <button
            type="button"
            disabled={busy || !enValue?.trim()}
            onClick={retranslate}
            className="px-3 py-1 text-[10px] text-mango hover:text-mango/80 hover:bg-mango/5 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`urdu-ai-${entity}-${typeof field === "string" ? field : "opt"}`}
            title="Ask Claude to draft this field with AI"
          >
            🪄 AI
          </button>
        </div>
      </div>
    </div>
  );
}
